/**
 * Initialize Swiper for a single Scratch Game gallery instance.
 *
 * @param {HTMLElement} gallery Gallery root element.
 * @return {void}
 */
const initScratchGameGallery = ( gallery ) => {

	if ( ! gallery || gallery.dataset.swiperInitialized === 'true' ) {

		return;

	}

	if ( typeof window.Swiper !== 'function' ) {

		return;

	}

	const mainSwiperEl = gallery.querySelector( '.b-scratch-game-intro-landscape__gallery-swiper' );

	if ( ! mainSwiperEl ) {

		return;

	}

	const prevEl = gallery.querySelector( '.b-scratch-game-intro-landscape__gallery-nav--prev' );
	const nextEl = gallery.querySelector( '.b-scratch-game-intro-landscape__gallery-nav--next' );
	const thumbsEl = gallery.querySelector( '.b-scratch-game-intro-landscape__gallery-thumbs' );

	let thumbsSwiper;

	if ( thumbsEl ) {

		const thumbsOptions = {
			spaceBetween: 10,
			slidesPerView: 4,
			freeMode: true,
			slideToClickedSlide: true,
			watchSlidesProgress: true,
			watchOverflow: true,
			allowTouchMove: true,
			simulateTouch: true,
			grabCursor: true,
			centerInsufficientSlides: true,
			breakpoints: {
				576: {
					spaceBetween: 20,
				},
			},
		};

		thumbsSwiper = new window.Swiper( thumbsEl, thumbsOptions );

	}

	const options = {
		slidesPerView: 1,
		spaceBetween: 20,
		speed: 400,
		autoHeight: true,
		observer: true,
		observeParents: true,
		resizeObserver: true,
		updateOnWindowResize: true,
		watchOverflow: true,
		allowTouchMove: true,
		simulateTouch: true,
		grabCursor: true,
		keyboard: {
			enabled: true,
			onlyInViewport: true,
			pageUpDown: true,
		},
		a11y: true,
	};

	if ( thumbsSwiper ) {

		options.thumbs = {
			swiper: thumbsSwiper,
		};

	}

	const mainSwiper = new window.Swiper( mainSwiperEl, options );
	const thumbSlides = thumbsEl
		? Array.from( thumbsEl.querySelectorAll( '.swiper-slide' ) )
		: [];

	/**
	 * Sync main Swiper container height to currently active slide.
	 *
	 * @return {void}
	 */
	const updateMainAutoHeight = () => {

		mainSwiper.updateAutoHeight( 0 );

	};

	/**
	 * Slide main gallery and keep focused thumbnail in sync.
	 *
	 * @param {number}  targetIndex            Slide index.
	 * @param {object}  [goToSlideOptions]            Options.
	 * @param {boolean} [goToSlideOptions.focusThumb] Whether to move focus to the target thumbnail. Default true.
	 * @return {void}
	 */
	const goToSlide = ( targetIndex, goToSlideOptions = {} ) => {

		const { focusThumb = true } = goToSlideOptions;

		if ( ! Number.isInteger( targetIndex ) ) {

			return;

		}

		const boundedIndex = Math.max( 0, Math.min( targetIndex, thumbSlides.length - 1 ) );

		mainSwiper.slideTo( boundedIndex );

		if ( focusThumb && thumbSlides[ boundedIndex ] instanceof HTMLElement ) {

			thumbSlides[ boundedIndex ].focus();

		}

	};

	/**
	 * Disable the thumb-nav prev/next buttons at the start/end of the gallery.
	 *
	 * @return {void}
	 */
	const updateThumbNavState = () => {

		if ( prevEl ) {

			const isFirst = mainSwiper.realIndex === 0;

			prevEl.classList.toggle( 'swiper-button-disabled', isFirst );
			prevEl.toggleAttribute( 'disabled', isFirst );

		}

		if ( nextEl ) {

			const isLast = mainSwiper.realIndex === thumbSlides.length - 1;

			nextEl.classList.toggle( 'swiper-button-disabled', isLast );
			nextEl.toggleAttribute( 'disabled', isLast );

		}

	};

	if ( prevEl ) {

		prevEl.addEventListener( 'click', () => {

			goToSlide( mainSwiper.realIndex - 1, { focusThumb: false } );

		} );

	}

	if ( nextEl ) {

		nextEl.addEventListener( 'click', () => {

			goToSlide( mainSwiper.realIndex + 1, { focusThumb: false } );

		} );

	}

	/**
	 * Keep thumbnail tab order and state aligned with active slide.
	 *
	 * @return {void}
	 */
	const updateThumbAccessibility = () => {

		if ( ! thumbSlides.length ) {

			return;

		}

		thumbSlides.forEach( ( slide, index ) => {

			const isActive = index === mainSwiper.realIndex;

			slide.setAttribute( 'role', 'button' );
			slide.setAttribute( 'aria-pressed', isActive ? 'true' : 'false' );
			slide.tabIndex = isActive ? 0 : -1;

		} );

	};

	if ( thumbSlides.length ) {

		thumbSlides.forEach( ( slide, index ) => {

			slide.addEventListener( 'keydown', ( event ) => {

				switch ( event.key ) {

				case 'Enter':
				case ' ':
				case 'Spacebar':
					event.preventDefault();
					goToSlide( index );
					break;
				case 'ArrowRight':
				case 'ArrowDown':
					event.preventDefault();
					goToSlide( index + 1 );
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					event.preventDefault();
					goToSlide( index - 1 );
					break;
				case 'Home':
					event.preventDefault();
					goToSlide( 0 );
					break;
				case 'End':
					event.preventDefault();
					goToSlide( thumbSlides.length - 1 );
					break;
				default:
					break;

				}

			} );

		} );

	}

	mainSwiper.on( 'init', updateMainAutoHeight );
	mainSwiper.on( 'slideChangeTransitionEnd', updateMainAutoHeight );
	mainSwiper.on( 'resize', updateMainAutoHeight );
	mainSwiper.on( 'slideChange', updateThumbAccessibility );
	mainSwiper.on( 'init', updateThumbNavState );
	mainSwiper.on( 'slideChange', updateThumbNavState );

	mainSwiperEl.querySelectorAll( 'img' ).forEach( ( image ) => {

		if ( image.complete ) {

			return;

		}

		image.addEventListener( 'load', updateMainAutoHeight, { once: true } );

	} );

	updateMainAutoHeight();
	updateThumbAccessibility();
	updateThumbNavState();

	gallery.dataset.swiperInitialized = 'true';

};

document.querySelectorAll( '.b-scratch-game-intro-landscape__gallery' )
	.forEach( initScratchGameGallery );
